// ==========================================================
// Predicción con IA — cuantía de procedimientos de selección
// ==========================================================
// Implementación del mockup «Prediccion Cuantia.dc.html» (proyecto de Claude
// Design `50366d9b-e3d0-469c-845d-4d8c2e5fd59c`), variante por defecto del
// enum de props: **tarjetas**. La otra variante, `lista`, quedó sin implementar.
//
// El postor busca su procedimiento entre los que el modelo ya analizó
// (tabla `predict_seace`), ve la confianza del modelo para ese ítem y pide la
// cuantía por WhatsApp. El precio, el plazo de entrega y la devolución que se
// garantiza si el valor cae fuera del rango están en PRECIO, ENTREGA y GARANTIA:
// son cifras comerciales que cambian, y cada una se repite en varios sitios de
// la pantalla.
//
// La cuantía NO se muestra: es lo que se vende. El monto borroso del hero y
// del modal es decorativo —`predict_seace` no guarda los montos, las dos
// columnas de monto referencial se dejaron fuera al cargar la tabla—, así que
// lo único real que se enseña gratis es `prob`.
//
// Estilos en `prediccion36.scss` (prefijo `pr36-`), con la paleta y las
// tipografías propias del mockup.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { documentosDeConvocatoria } from '../services/anexosConvocatoria';
import './prediccion36.scss';

const TABLA = 'predict_seace';
const WHATSAPP = '51942128139';
const WHATSAPP_VISIBLE = '942 128 139';
const PRECIO = 60;

// Las dos promesas del servicio. Van en constantes porque cada una se repite en
// tres sitios —hero, tarjetas de respaldo y columna de compra del modal— y
// escritas a mano se quedaban descuadradas entre sí al cambiar una.
const GARANTIA = 100;        // % del costo que se devuelve si el valor cae fuera del rango
const ENTREGA = '5-10 minutos';

// Tope de tarjetas por búsqueda. Con más, la rejilla deja de ser útil y
// conviene que el usuario afine el término.
const LIMITE = 60;

// Columnas que la pantalla necesita. Las que el scraper escribió en mayúsculas
// o en camelCase van entre comillas: PostgREST las exige para respetarlas.
const COLUMNAS = [
  'id',
  'id_procedimiento',
  'nomenclatura',
  'entidad',
  'tipo_proceso',
  'objeto',
  'moneda',
  'detalle_item',
  'descripcion_proceso',
  'departamento',
  'provincia',
  'distrito',
  'fecha_convocatoria',
  'fecha_inicio_participantes',
  'fecha_fin_participantes',
  'fecha_presentacion_propuestas',
  'prob',
  'url_doc',
  'url_anexos',
  '"cantItem"',
  '"detUnidadMedida"',
  '"detCubso"',
  '"N_ITEM"'
].join(',');

const OBJETOS = [
  { id: '', label: 'Todos' },
  { id: 'Bien', label: 'Bienes' },
  { id: 'Servicio', label: 'Servicios' }
];

const VIGENCIAS = [
  { id: 'abiertas', label: 'En plazo' },
  { id: 'vencidas', label: 'Vencidas' },
  { id: 'todas', label: 'Todas' }
];

// Atajos del buscador. Están medidos contra los datos cargados hoy: los cinco
// devuelven resultados (mantenimiento ~236 ítems, alimentos ~168, vigilancia
// ~73, limpieza ~68, combustible ~15).
const SUGERENCIAS = ['Mantenimiento', 'Alimentos', 'Limpieza', 'Vigilancia', 'Combustible'];

// Cifra decorativa de la caja difuminada. No es una predicción: sólo da al
// desenfoque una forma creíble de monto.
const MONTO_TAPADO = 'S/ 1 284 560,00';

// Documentos de la convocatoria: las bases (`url_doc`) y los anexos que sube la
// entidad (`url_anexos`), los dos con el mismo parser que el SEACE. Las bases
// las trae todo el Excel del modelo, los anexos solo 344 filas de 1930 —pero
// cuando los trae pueden ser muchos (el máximo cargado son 131 archivos en una
// convocatoria), así que la lista se recorta y se despliega a pedido.
const DOCS_VISIBLES = 5;

// ==========================================================
// Utilidades de fecha
// ==========================================================
// `fecha_fin_participantes` es un timestamp sin zona y los datos vienen en hora
// de Perú, así que se compara contra la hora de Lima en texto, no contra un
// ISO en UTC (serían cinco horas de diferencia).
const ahoraEnLima = () => {
  const partes = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Lima',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).format(new Date());
  return partes.replace(' ', 'T');
};

// Un timestamp sin zona se arma componente por componente: `new Date(texto)`
// lo leería como UTC en algunos navegadores y correría la fecha un día.
const parsearFecha = (valor) => {
  if (!valor) return null;
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatearFecha = (valor) => {
  const d = parsearFecha(valor);
  if (!d) return '—';
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatearFechaHora = (valor) => {
  const d = parsearFecha(valor);
  if (!d) return '—';
  const hora = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${formatearFecha(valor)} · ${hora}`;
};

// Días completos que faltan, contados de medianoche a medianoche.
const diasRestantes = (valor) => {
  const d = parsearFecha(valor);
  if (!d) return null;
  const hoy = parsearFecha(ahoraEnLima());
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const b = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((a - b) / 86400000);
};

const estadoPlazo = (valor) => {
  const dias = diasRestantes(valor);
  if (dias === null) return { clase: '', texto: 'Sin fecha' };
  if (dias < 0) return { clase: 'is-vencido', texto: 'Plazo vencido' };
  if (dias === 0) return { clase: 'is-hoy', texto: 'Cierra hoy' };
  if (dias === 1) return { clase: 'is-hoy', texto: 'Cierra mañana' };
  if (dias <= 7) return { clase: 'is-pronto', texto: `Cierra en ${dias} días` };
  return { clase: '', texto: `Cierra en ${dias} días` };
};

// ==========================================================
// Cronograma del procedimiento
// ==========================================================
// Las cuatro fechas vienen siempre completas y en orden en las 1 803 filas
// (convocatoria → apertura del registro → cierre del registro → ofertas), así
// que la línea de tiempo se puede dar por buena sin reordenarla.
//
// `fecha_presentacion_propuestas` es el único timestamp CON zona (llega como
// 00:01+00). Se muestra sólo el día: la hora en UTC confundiría.
const HITOS = [
  { campo: 'fecha_convocatoria', label: 'Convocatoria', conHora: true },
  { campo: 'fecha_inicio_participantes', label: 'Registro de participantes', conHora: true },
  { campo: 'fecha_fin_participantes', label: 'Cierre del registro', conHora: true },
  { campo: 'fecha_presentacion_propuestas', label: 'Presentación de ofertas', conHora: false }
];

// Etiqueta corta para lo que todavía no ocurre.
const cuentaRegresiva = (dias) => {
  if (dias === null) return null;
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Mañana';
  return `En ${dias} días`;
};

const construirCronograma = (fila) => {
  const ahora = parsearFecha(ahoraEnLima());
  let yaHayProximo = false;

  return HITOS.map(({ campo, label, conHora }) => {
    const fecha = parsearFecha(fila[campo]);
    const cumplido = fecha ? fecha <= ahora : false;
    const dias = diasRestantes(fila[campo]);

    // El primer hito que aún no ocurre es el que la pantalla resalta.
    let estado = 'pendiente';
    if (cumplido) {
      estado = 'cumplido';
    } else if (!yaHayProximo) {
      estado = 'ahora';
      yaHayProximo = true;
    }

    // El hito destacado toma el color de urgencia; los demás van neutros.
    let tono = '';
    if (estado === 'ahora') {
      if (dias !== null && dias <= 1) tono = 'is-hoy';
      else if (dias !== null && dias <= 7) tono = 'is-pronto';
    }

    return {
      campo,
      label,
      estado,
      tono,
      texto: conHora ? formatearFechaHora(fila[campo]) : formatearFecha(fila[campo]),
      falta: cumplido ? null : cuentaRegresiva(dias)
    };
  });
};

// ==========================================================
// Confianza del modelo (columna `prob`)
// ==========================================================
const formatearProb = (prob) => {
  const n = Number(prob);
  return Number.isFinite(n) ? n.toFixed(1).replace('.', ',') : '—';
};

const nivelConfianza = (prob) => {
  const n = Number(prob);
  if (!Number.isFinite(n)) return 'media';
  if (n >= 93) return 'muy alta';
  if (n >= 85) return 'alta';
  return 'media';
};

// Los valores van de 75 a 98: pintar la barra con el porcentaje crudo las
// dejaría todas casi llenas, así que se reescala el tramo 70-100.
const proporcion = (prob) => {
  const n = Number(prob);
  if (!Number.isFinite(n)) return 0;
  return Math.max(6, Math.min(100, ((n - 70) / 30) * 100));
};

// ==========================================================
// Término de búsqueda
// ==========================================================
// En `or()` la coma separa condiciones y los paréntesis la delimitan, así que
// esos caracteres (y los comodines) se cambian por espacios.
const limpiarPatron = (texto) => texto.replace(/[,()%*\\]/g, ' ').trim();

// El SEACE escribe la misma palabra con y sin tilde —«asfáltica» aparece 3
// veces y «asfaltica» 6—, y nadie teclea tildes al buscar. Cada vocal pasa a
// ser `_`, el comodín de un carácter de LIKE, para que ambas formas entren.
// Es menos fino que `unaccent` (un `_` casa con cualquier letra), pero no
// necesita extensión ni función en la base y con palabras largas no estorba.
const insensibleATildes = (palabra) => palabra.replace(/[aeiouáéíóúüAEIOUÁÉÍÓÚÜ]/g, '_');

// Las palabras de dos letras o menos ("de", "la", "y") están en casi todas las
// filas y sólo alargan la consulta; el tope de seis evita URLs enormes.
const MAX_PALABRAS = 6;

const palabrasDeBusqueda = (texto) => {
  const limpio = limpiarPatron(texto);
  if (!limpio) return [];
  const palabras = limpio.split(/\s+/).filter((p) => p.length > 2);
  return (palabras.length ? palabras : [limpio]).slice(0, MAX_PALABRAS);
};

// ==========================================================
// Enlaces de WhatsApp
// ==========================================================
const enlaceWhatsApp = (fila, correo) => {
  const lineas = [
    `Hola, quiero solicitar la predicción de cuantía (S/ ${PRECIO}) de este procedimiento:`,
    '',
    `• Nomenclatura: ${fila.nomenclatura || '—'}`,
    `• Código de convocatoria: ${fila.id_procedimiento ?? '—'}`,
    `• Entidad: ${fila.entidad || '—'}`,
    `• Ítem ${fila.N_ITEM ?? '—'}: ${fila.detalle_item || '—'}`,
    `• Confianza del modelo: ${formatearProb(fila.prob)} %`,
    ''
  ];
  if (correo) lineas.push(`Escribo desde LicitaHUB con la cuenta ${correo}.`);
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lineas.join('\n'))}`;
};

const enlaceWhatsAppGeneral = (correo) => {
  const lineas = [
    `Hola, quiero información sobre la predicción de cuantía de LicitaHUB (S/ ${PRECIO} por procedimiento).`
  ];
  if (correo) lineas.push('', `Escribo con la cuenta ${correo}.`);
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lineas.join('\n'))}`;
};

// ==========================================================
// Iconos (los del mockup, en línea para no mezclar dos juegos de iconos)
// ==========================================================
const IconoChispa = (p) => (
  <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
    <path d="M19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </svg>
);

const IconoCandado = (p) => (
  <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

// El mismo candado, abierto: marca lo que ya se entregó frente a lo que está
// bloqueado en el resto de la pantalla.
const IconoCandadoAbierto = (p) => (
  <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 017.5-2" />
  </svg>
);

const IconoEscudo = (p) => (
  <svg width={p.size || 15} height={p.size || 15} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
    <path d="M8.5 12l2.5 2.5 4.5-5" />
  </svg>
);

const IconoLupa = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

const IconoEquis = (p) => (
  <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconoPin = (p) => (
  <svg width={p.size || 13} height={p.size || 13} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const IconoReloj = (p) => (
  <svg width={p.size || 12} height={p.size || 12} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const IconoCheck = (p) => (
  <svg width={p.size || 13} height={p.size || 13} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const IconoExpediente = (p) => (
  <svg width={p.size || 15} height={p.size || 15} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
);

const IconoCaja = (p) => (
  <svg width={p.size || 15} height={p.size || 15} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 8l-9-5-9 5 9 5 9-5z" />
    <path d="M3 8v8l9 5 9-5V8M12 13v8" />
  </svg>
);

const IconoCalendario = (p) => (
  <svg width={p.size || 15} height={p.size || 15} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

const IconoClip = (p) => (
  <svg width={p.size || 15} height={p.size || 15} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 11.5l-8.1 8.1a5 5 0 01-7.1-7.1l8.5-8.5a3.3 3.3 0 014.7 4.7l-8.5 8.5a1.7 1.7 0 01-2.4-2.4l7.8-7.8" />
  </svg>
);

// Un solo icono de archivo para los ocho tipos del parser: el tipo ya se dice
// con palabras en el subtítulo y con el color del recuadro, y meter el juego de
// iconos de react-icons rompería el criterio de esta pantalla (ver arriba).
const IconoArchivo = (p) => (
  <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const IconoSalida = (p) => (
  <svg width={p.size || 14} height={p.size || 14} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4h6v6M20 4l-8.5 8.5" />
    <path d="M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" />
  </svg>
);

const IconoWhatsApp = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 00-8.6 15.1L2 22l5-1.3A10 10 0 1012 2zm0 18.2a8.2 8.2 0 01-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1112 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 01-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 00-.7.3 3 3 0 00-.9 2.2 5.2 5.2 0 001.1 2.7 11.8 11.8 0 004.5 4c1.7.7 2.3.8 3.2.6a2.7 2.7 0 001.7-1.2 2.2 2.2 0 00.2-1.2c-.1-.1-.3-.2-.5-.3z" />
  </svg>
);

// Límite inferior y superior: es la forma de lo que se entrega, un rango y no
// una cifra suelta.
const IconoRango = (p) => (
  <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7v10M20 7v10M4 12h16" />
    <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
  </svg>
);

// ==========================================================
// Las tres cifras que respaldan la predicción
// ==========================================================
// No se calculan de `predict_seace`: son las del servicio. La precisión y el
// intervalo salen del entrenamiento del modelo y el 70 % es la devolución que
// se garantiza. La columna `prob` de cada ítem es otra cosa —la confianza de
// esa predicción en concreto— y se sigue mostrando en su tarjeta.
// `pct` sirve dos veces: es la cifra que se lee y el ancho de la barra, que es
// lo que deja comparar las tres de un vistazo.
const VALORES = [
  {
    pct: 90,
    label: 'Precisión garantizada',
    titulo: 'IA especializada',
    texto: 'Modelo predictivo entrenado con más de 50 000 licitaciones reales del Estado '
      + 'peruano para máxima precisión.',
    Icono: IconoChispa
  },
  {
    pct: 95,
    label: 'Nivel de confianza',
    titulo: 'Rango predictivo',
    // Sin la afirmación estadística de antes («intervalo de confianza sobre datos
    // históricos»): lo que se promete ahora es la revisión de cada predicción, que es
    // una promesa que se cumple y se puede defender.
    texto: 'Todas las predicciones que entregamos fueron evaluadas de manera exhaustiva: '
      + 'el rango que recibe el postor pasa antes por esa revisión.',
    Icono: IconoRango
  },
  {
    pct: GARANTIA,
    label: 'Garantía',
    titulo: 'Garantía total',
    texto: `Devolución automática del ${GARANTIA} % del costo si el valor final cae fuera `
      + 'del rango que predijo el sistema.',
    Icono: IconoEscudo
  }
];

// ==========================================================
// Casos entregados
// ==========================================================
// Predicciones reales, con el rango que recibió el postor. Es el único sitio de
// la pantalla donde la cuantía se ve sin difuminar, y por eso convence: enseña
// exactamente lo que se compra.
//
// Los datos se escriben a mano porque `predict_seace` no guarda montos (se
// excluyeron al cargarla a propósito). No se inventan: cada caso tiene que
// venir de una predicción realmente entregada, o deja de ser una prueba.
// `prob` sale de la fila del procedimiento en `predict_seace`, no se escribe a
// ojo: es la misma cifra que el postor ve en la tarjeta de ese ítem cuando lo
// busca, y eso es lo que ata el caso con lo que ofrece la pantalla.
// `buenaPro` es opcional: no todos los casos la tienen a mano.
const CASOS = [
  {
    nomenclatura: 'LP-ABR-311-2026-GR PUNO/OC-1',
    entidad: 'GOBIERNO REGIONAL DE PUNO SEDE CENTRAL',
    objeto: 'Bien',
    descripcion: 'ADQUISICIÓN DE GRUPO ELECTRÓGENO INCLUYE INSTALACION SEGÚN REQUERIMIENTO '
      + 'Y ESPECIFICACIONES TECNICAS PARA LA META: MEJORAMIENTO DE SERVICIO EDUCATIVO DE LAS '
      + 'CARRERAS DE PRODUCCION AUTOMOTRIZ, MINERIA, CONTABILIDAD, COMPUTACION, SECRETARIADO, '
      + 'ENFERMERIA, LABORATORIO CLINICO Y PROTESIS DENTAL DEL IST',
    propuestas: '05/11/2026',
    buenaPro: '06/11/2026',
    prob: 93.73,
    rango: [338000, 352050]
  },
  {
    nomenclatura: 'LP-ABR-51-2026-CS/MPJB-1',
    entidad: 'MUNICIPALIDAD PROVINCIAL DE JORGE BASADRE - LOCUMBA',
    objeto: 'Bien',
    descripcion: 'ADQUISICIÓN DE MEZCLADORA HORIZONTAL PARA EL PLAN DE NEGOCIO DE PROCOMPITE '
      + '2025: MEJORAMIENTO DE LA PRODUCCION DE LECHE DE VACUNO EN LA ASOCIACION DE PRODUCTORES '
      + 'EXPORTADORES AGROPECUARIOS DE CHAUCALANA, DEL DISTRITO DE LOCUMBA, PROVINCIA JORGE '
      + 'BASADRE, REGION TACNA',
    propuestas: '06/11/2026',
    buenaPro: null,
    prob: 93.98,
    rango: [72300, 80120]
  },
  {
    nomenclatura: 'LP-ABR-4-2026-MDSL/CS-1',
    entidad: 'MUNICIPALIDAD DISTRITAL DE SANTA LUCIA - LAMPA',
    objeto: 'Bien',
    descripcion: 'ADQUISICIÓN DE CHALECOS TÉRMICOS CON FORRO INTERNO DE POLAR PARA CRÍAS DE '
      + 'ALPACA EN CUMPLIMIENTO DEL COMPONENTE 03, ACCIÓN 04: ADQUISICIÓN DE INDUMENTARIA DE '
      + 'PROTECCIÓN CONTRA EL FRIO, DEL PROYECTO DENOMINADO: MEJORAMIENTO DE LOS SERVICIOS DE '
      + 'APOYO AL DESARROLLO PRODUCTIVO EN EL USO SOSTENIBLE DE L',
    propuestas: '06/11/2026',
    buenaPro: null,
    prob: 97.26,
    // OJO: tal como se pasó. El extremo inferior está pendiente de confirmar —
    // 14 950 a 163 500 es un rango de casi 11 veces, cuando los otros dos casos
    // abren un 4 % y un 11 %; todo apunta a un cero perdido en 149 500.
    rango: [149500, 163500]
  }
];

// Sin decimales: los dos extremos del rango son cifras redondas y con «,00»
// detrás la caja se vuelve ilegible en una tarjeta estrecha.
const soles = (n) => `S/ ${Number(n).toLocaleString('es-PE', { maximumFractionDigits: 0 })}`;

// Caja con la cuantía difuminada. El monto es decorativo (ver cabecera).
const CuantiaTapada = ({ texto, className }) => (
  <div className={className || 'pr36-tapado'}>
    <div className="pr36-tapado-cifra" aria-hidden="true">{MONTO_TAPADO}</div>
    <div className="pr36-tapado-texto">{texto}</div>
  </div>
);

// ==========================================================
// Componente
// ==========================================================
const PrediccionLicitaciones = ({ user }) => {
  const correo = user?.email || '';

  const [termino, setTermino] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [objeto, setObjeto] = useState('');
  const [vigencia, setVigencia] = useState('abiertas');

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [resumen, setResumen] = useState({ total: 0, promedio: null });
  const [departamentos, setDepartamentos] = useState([]);

  const [seleccionada, setSeleccionada] = useState(null);
  const [verTodosDocs, setVerTodosDocs] = useState(false);

  // Cada búsqueda lleva su número de orden: si una lenta responde después de
  // una más nueva, se descarta en vez de sobreescribir la rejilla.
  const consultaRef = useRef(0);

  // ----------------------------------------------------------
  // Carga única: departamentos y cifras del hero
  // ----------------------------------------------------------
  useEffect(() => {
    let vivo = true;

    (async () => {
      // PostgREST corta en 1000 filas por respuesta (max-rows), así que la
      // tabla se recorre por tramos: con un `limit` grande se perderían las
      // filas a partir de la mil y los totales saldrían cortos.
      const TRAMO = 1000;
      const data = [];
      for (let desde = 0; ; desde += TRAMO) {
        const { data: lote, error: err } = await supabase
          .from(TABLA)
          .select('departamento,prob')
          .range(desde, desde + TRAMO - 1);

        if (!vivo || err || !lote) return;
        data.push(...lote);
        if (lote.length < TRAMO) break;
      }

      if (!vivo) return;

      let suma = 0;
      const deps = new Set();
      data.forEach((r) => {
        if (r.departamento) deps.add(r.departamento);
        suma += Number(r.prob) || 0;
      });

      setResumen({ total: data.length, promedio: data.length ? suma / data.length : null });
      setDepartamentos([...deps].sort((a, b) => a.localeCompare(b, 'es')));
    })();

    return () => { vivo = false; };
  }, []);

  // ----------------------------------------------------------
  // Búsqueda
  // ----------------------------------------------------------
  const buscar = useCallback(async () => {
    const turno = ++consultaRef.current;
    setCargando(true);
    setError(null);

    let consulta = supabase.from(TABLA).select(COLUMNAS, { count: 'exact' });

    const texto = termino.trim();
    if (texto) {
      // Un número largo es el código de convocatoria; lo demás es texto libre.
      if (/^\d{5,}$/.test(texto)) {
        consulta = consulta.eq('id_procedimiento', Number(texto));
      } else {
        // Se busca palabra por palabra, no la frase entera: nadie escribe la
        // descripción literal del ítem. Cada `or()` encadenado se combina con
        // AND, así que salen las filas donde están todas las palabras, cada
        // una en cualquiera de las cuatro columnas.
        for (const palabra of palabrasDeBusqueda(texto)) {
          const patron = `%${insensibleATildes(palabra)}%`;
          consulta = consulta.or(
            [
              `nomenclatura.ilike.${patron}`,
              `entidad.ilike.${patron}`,
              `detalle_item.ilike.${patron}`,
              `descripcion_proceso.ilike.${patron}`
            ].join(',')
          );
        }
      }
    }

    if (departamento) consulta = consulta.eq('departamento', departamento);
    if (objeto) consulta = consulta.eq('objeto', objeto);

    if (vigencia === 'abiertas') {
      consulta = consulta.gte('fecha_fin_participantes', ahoraEnLima());
    } else if (vigencia === 'vencidas') {
      consulta = consulta.lt('fecha_fin_participantes', ahoraEnLima());
    }

    const { data, count, error: err } = await consulta
      .order('prob', { ascending: false })
      .limit(LIMITE);

    if (turno !== consultaRef.current) return; // llegó tarde

    if (err) {
      setError(err.message || 'No se pudo consultar la base.');
      setFilas([]);
      setTotal(0);
    } else {
      setFilas(data || []);
      setTotal(count ?? (data ? data.length : 0));
    }
    setCargando(false);
  }, [termino, departamento, objeto, vigencia]);

  // El término se deja reposar; los filtros de lista disparan al instante.
  useEffect(() => {
    const espera = termino.trim() ? 350 : 0;
    const t = setTimeout(buscar, espera);
    return () => clearTimeout(t);
  }, [buscar, termino]);

  // Con el modal abierto: Escape lo cierra y la rejilla de atrás no debe
  // moverse con la rueda del ratón.
  useEffect(() => {
    if (!seleccionada) return undefined;
    setVerTodosDocs(false); // cada convocatoria abre con la lista recortada
    const alTeclear = (e) => { if (e.key === 'Escape') setSeleccionada(null); };
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', alTeclear);
    return () => {
      document.body.style.overflow = overflowPrevio;
      window.removeEventListener('keydown', alTeclear);
    };
  }, [seleccionada]);

  const hayFiltros = Boolean(termino.trim() || departamento || objeto) || vigencia !== 'abiertas';

  const limpiarTodo = () => {
    setTermino('');
    setDepartamento('');
    setObjeto('');
    setVigencia('abiertas');
  };

  // El `window.open` va dentro del propio gesto del clic: después de un await
  // el navegador lo trataría como emergente y lo bloquearía.
  const abrirDoc = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Datos derivados del ítem abierto en el modal.
  const sel = useMemo(() => {
    if (!seleccionada) return null;
    const f = seleccionada;
    const grados = Math.round((proporcion(f.prob) / 100) * 360);
    return {
      plazo: estadoPlazo(f.fecha_fin_participantes),
      esServicio: String(f.objeto || '').toLowerCase().startsWith('serv'),
      nivel: nivelConfianza(f.prob),
      probTxt: formatearProb(f.prob),
      cronograma: construirCronograma(f),
      // Bases primero y anexos después, ya validados: el parser descarta lo que
      // no sea una URL http(s), y hay filas con «Base no disponible» en vez de
      // enlace (5 de 1930, igual que en `seace`).
      docs: documentosDeConvocatoria(f),
      anillo: `conic-gradient(#a9d8c9 ${grados}deg, rgba(255,255,255,.14) 0)`,
      lugar: [f.departamento, f.provincia, f.distrito].filter(Boolean).join(' · ') || '—',
      itemTxt: `N° ${f.N_ITEM ?? '—'}${
        f.cantItem != null
          ? ` · ${Number(f.cantItem).toLocaleString('es-PE')} ${f.detUnidadMedida || ''}`.trimEnd()
          : ''
      }`
    };
  }, [seleccionada]);

  return (
    <div className="pr36">

      {/* ---------- Hero ---------- */}
      <section className="pr36-hero">
        <div className="pr36-hero-info">
          <div className="pr36-hero-eyebrow">
            <IconoChispa />
            Predicción de cuantía con IA
          </div>
          <h1 className="pr36-hero-titulo">
            Conoce el valor de la cuantía de tu licitación
          </h1>
          <p className="pr36-hero-gancho">No pierdas puntos por cotizar muy alto.</p>
          <p className="pr36-hero-sub">
            Obtén el rango recomendado de la cuantía del proceso en el que estás participando, en{' '}
            {ENTREGA}. Para eso entrenamos un modelo estadístico con más de 50 000 licitaciones reales del
            SEACE: para saber cuánto vale la cuantía antes de que presentes tu oferta.
          </p>
          <div className="pr36-hero-pasos">
            <div className="pr36-paso-chip"><span>1</span>Busca tu convocatoria</div>
            <div className="pr36-paso-chip"><span>2</span>Solicítala por WhatsApp</div>
            <div className="pr36-paso-chip"><span>3</span>Recibe el rango en {ENTREGA}</div>
          </div>
        </div>

        <div className="pr36-precio">
          <div className="pr36-precio-cab">
            <span className="pr36-precio-label">Cuantía estimada</span>
            <span className="pr36-precio-candado"><IconoCandado />Bloqueada</span>
          </div>

          <CuantiaTapada texto="Rango de valor referencial del ítem" />

          <div className="pr36-precio-fila">
            <div className="pr36-precio-monto">
              <span className="pr36-precio-cifra">S/ {PRECIO}</span>
              <span className="pr36-precio-unidad">por procedimiento</span>
            </div>
            <span className="pr36-precio-garantia">
              <IconoEscudo />Devolución {GARANTIA} %
            </span>
          </div>

          {/* Atajo para quien ya sabe lo que quiere y no va a buscar primero:
              lleva el mensaje general, sin convocatoria, porque desde el hero
              todavía no hay ninguna elegida. */}
          <a
            className="pr36-wa"
            href={enlaceWhatsAppGeneral(correo)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconoWhatsApp size={18} />
            Solicitar mi cuantía por WhatsApp
          </a>

          <div className="pr36-precio-pie">
            <span>
              <strong>{resumen.total ? resumen.total.toLocaleString('es-PE') : '—'}</strong>{' '}
              ítems analizados
            </span>
            <span>
              <strong>{resumen.promedio !== null ? `${formatearProb(resumen.promedio)} %` : '—'}</strong>{' '}
              confianza promedio
            </span>
          </div>
        </div>
      </section>

      {/* ---------- Respaldo: las tres cifras del servicio ---------- */}
      <section className="pr36-valores" aria-label="Cómo se calcula la cuantía">
        {VALORES.map(({ pct, label, titulo, texto, Icono }) => (
          <article className="pr36-valor" key={titulo}>
            <div className="pr36-valor-cab">
              {/* El anillo es el mismo recurso que el de confianza del modal:
                  el arco dibuja la cifra y el icono cuelga del borde. */}
              <div className="pr36-valor-marca">
                <div className="pr36-valor-anillo" style={{ '--arco': `${pct * 3.6}deg` }}>
                  <span>{pct}<i>%</i></span>
                </div>
                <span className="pr36-valor-ic"><Icono size={15} /></span>
              </div>

              <div className="pr36-valor-encab">
                <h3 className="pr36-valor-titulo">{titulo}</h3>
                <div className="pr36-valor-label">{label}</div>
              </div>
            </div>

            <p className="pr36-valor-texto">{texto}</p>
          </article>
        ))}
      </section>

      {/* ---------- Casos entregados ---------- */}
      {CASOS.length > 0 && (
        <section className="pr36-casos">
          <div className="pr36-seccion-cab">
            <h2 className="pr36-seccion-titulo">Predicciones ya entregadas</h2>
            <p className="pr36-seccion-sub">
              Esto es exactamente lo que recibe el postor: el rango de la cuantía del
              procedimiento, sin difuminar.
            </p>
          </div>

          <div className="pr36-casos-grid">
            {CASOS.map((caso) => (
              <article className="pr36-caso" key={caso.nomenclatura}>
                <div className="pr36-caso-cab">
                  <span className="pr36-caso-nom">{caso.nomenclatura}</span>
                  <span className={`pr36-tag ${caso.objeto === 'Servicio' ? 'is-serv' : 'is-bien'}`}>
                    {caso.objeto}
                  </span>
                </div>

                <div className="pr36-caso-entidad">{caso.entidad}</div>
                <p className="pr36-caso-desc">{caso.descripcion}</p>

                {/* Misma caja que la cuantía tapada del hero, pero destapada:
                    el contraste entre las dos es el argumento de venta. */}
                <div className="pr36-caso-rango">
                  <div className="pr36-caso-rango-cab">
                    <span className="pr36-caso-rango-label">
                      <IconoCandadoAbierto size={13} />
                      Cuantía predicha
                    </span>
                    <span className="pr36-caso-rango-prob">
                      {formatearProb(caso.prob)} % confianza
                    </span>
                  </div>
                  <div className="pr36-caso-rango-cifras">
                    <span>{soles(caso.rango[0])}</span>
                    <em>—</em>
                    <span>{soles(caso.rango[1])}</span>
                  </div>
                </div>

                <div className="pr36-caso-pie">
                  <span>
                    <IconoCalendario size={12} />
                    Propuestas <strong>{caso.propuestas}</strong>
                  </span>
                  {caso.buenaPro && (
                    <span>
                      <IconoCheck size={12} />
                      Buena Pro <strong>{caso.buenaPro}</strong>
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Buscador ---------- */}
      <section className="pr36-buscador">
        <div className="pr36-campo">
          <IconoLupa />
          <input
            type="text"
            value={termino}
            onChange={(e) => setTermino(e.target.value)}
            placeholder="Nomenclatura, entidad, descripción o código de convocatoria"
            aria-label="Buscar convocatoria"
          />
          {termino && (
            <button
              type="button"
              className="pr36-limpiar"
              onClick={() => setTermino('')}
              aria-label="Limpiar"
            >
              <IconoEquis />
            </button>
          )}
        </div>

        <div className="pr36-filtros">
          <div className="pr36-filtros-izq">
            <select
              className="pr36-select"
              value={departamento}
              onChange={(e) => setDepartamento(e.target.value)}
              aria-label="Departamento"
            >
              <option value="">Todo el Perú</option>
              {departamentos.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <div className="pr36-segmento" role="group" aria-label="Objeto de la contratación">
              {OBJETOS.map((o) => (
                <button
                  key={o.id || 'todos'}
                  type="button"
                  className={objeto === o.id ? 'is-activo' : ''}
                  onClick={() => setObjeto(o.id)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pr36-segmento" role="group" aria-label="Vigencia del plazo">
            {VIGENCIAS.map((v) => (
              <button
                key={v.id}
                type="button"
                className={vigencia === v.id ? 'is-activo' : ''}
                onClick={() => setVigencia(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pr36-sugerencias">
          <span>Prueba:</span>
          {SUGERENCIAS.map((s) => (
            <button key={s} type="button" onClick={() => setTermino(s)}>{s}</button>
          ))}
        </div>
      </section>

      {/* ---------- Resumen ---------- */}
      <div className="pr36-resumen">
        <div className="pr36-resumen-total">
          {cargando ? (
            'Buscando…'
          ) : (
            <>
              <strong>{total.toLocaleString('es-PE')}</strong>{' '}
              {total === 1 ? 'convocatoria con cuantía lista' : 'convocatorias con cuantía lista'}
            </>
          )}
        </div>
        <div className="pr36-resumen-der">
          <span>
            Ordenado por confianza del modelo
            {!cargando && total > LIMITE && <> · se muestran las {LIMITE} primeras</>}
          </span>
          {hayFiltros && (
            <button type="button" className="pr36-resumen-quitar" onClick={limpiarTodo}>
              Quitar filtros
            </button>
          )}
        </div>
      </div>

      {/* ---------- Resultados ---------- */}
      {cargando && (
        <section className="pr36-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="pr36-skeleton">
              <span /><span /><span /><span /><span />
            </div>
          ))}
        </section>
      )}

      {!cargando && error && (
        <div className="pr36-vacio">
          <h3>No se pudo cargar la información</h3>
          <p>{error}</p>
        </div>
      )}

      {!cargando && !error && filas.length === 0 && (
        <div className="pr36-vacio">
          <h3>No encontramos esa convocatoria</h3>
          <p>
            Prueba con menos palabras, el nombre de la entidad o el código de convocatoria. Si no
            está, escríbenos y la analizamos.
          </p>
          <a
            className="pr36-btn-wa"
            href={enlaceWhatsAppGeneral(correo)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Escribir al {WHATSAPP_VISIBLE}
          </a>
        </div>
      )}

      {!cargando && !error && filas.length > 0 && (
        <section className="pr36-grid">
          {filas.map((fila) => {
            const plazo = estadoPlazo(fila.fecha_fin_participantes);
            const nivel = nivelConfianza(fila.prob);
            const esServicio = String(fila.objeto || '').toLowerCase().startsWith('serv');

            return (
              <article
                key={fila.id}
                className="pr36-card"
                role="button"
                tabIndex={0}
                onClick={() => setSeleccionada(fila)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSeleccionada(fila);
                  }
                }}
              >
                <div className="pr36-card-cab">
                  <span className="pr36-card-nom">{fila.nomenclatura}</span>
                  <span className={`pr36-tag ${esServicio ? 'is-serv' : 'is-bien'}`}>
                    {fila.objeto || '—'}
                  </span>
                </div>

                <h3 className="pr36-card-titulo">{fila.detalle_item}</h3>
                <div className="pr36-card-entidad">{fila.entidad}</div>

                <div className="pr36-card-metas">
                  <span className="pr36-lugar">
                    <IconoPin />
                    {[fila.departamento, fila.provincia].filter(Boolean).join(' · ') || '—'}
                  </span>
                  <span className={`pr36-plazo ${plazo.clase}`}>
                    <IconoReloj />
                    {plazo.texto}
                  </span>
                </div>

                <div className="pr36-card-pie">
                  <div className="pr36-conf">
                    <div className="pr36-conf-top">
                      <span className="pr36-conf-valor">{formatearProb(fila.prob)} %</span>
                      <span className="pr36-conf-nivel">confianza {nivel}</span>
                    </div>
                    <div className="pr36-conf-barra">
                      <div
                        className={`pr36-conf-relleno${nivel === 'media' ? ' is-media' : ''}`}
                        style={{ width: `${proporcion(fila.prob)}%` }}
                      />
                    </div>
                  </div>
                  <span className="pr36-ver"><IconoCandado size={13} />Ver cuantía</span>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {/* ---------- Modal ---------- */}
      {seleccionada && sel && (
        <div
          className="pr36-modal-fondo"
          onClick={() => setSeleccionada(null)}
          role="presentation"
        >
          <div
            className="pr36-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Detalle de la convocatoria"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pr36-modal-cab">
              <div className="pr36-modal-cab-info">
                <div className="pr36-modal-cab-tags">
                  <span className="pr36-modal-cab-nom">{seleccionada.nomenclatura}</span>
                  <span className={`pr36-tag ${sel.esServicio ? 'is-serv' : 'is-bien'}`}>
                    {seleccionada.objeto || '—'}
                  </span>
                  <span className={`pr36-plazo ${sel.plazo.clase}`}>{sel.plazo.texto}</span>
                </div>
                <h2>{seleccionada.detalle_item}</h2>
                <div className="pr36-modal-cab-entidad">{seleccionada.entidad}</div>
              </div>
              <button
                type="button"
                className="pr36-modal-cerrar"
                onClick={() => setSeleccionada(null)}
                aria-label="Cerrar"
              >
                <IconoEquis size={16} />
              </button>
            </header>

            <div className="pr36-modal-cuerpo">
              {/* Ficha del procedimiento */}
              <div className="pr36-ficha">
                {/* Las fechas ya no van aquí: están todas en el cronograma. */}
                <div className="pr36-datos">
                  <div className="pr36-dato is-verde">
                    <span className="pr36-dato-icono"><IconoExpediente /></span>
                    <div>
                      <div className="pr36-dato-label">Procedimiento</div>
                      <div className="pr36-dato-valor">{seleccionada.tipo_proceso || '—'}</div>
                    </div>
                  </div>
                  <div className="pr36-dato is-azul">
                    <span className="pr36-dato-icono"><IconoCaja /></span>
                    <div>
                      <div className="pr36-dato-label">Ítem</div>
                      <div className="pr36-dato-valor">{sel.itemTxt}</div>
                    </div>
                  </div>
                </div>

                {/* Cronograma */}
                <div className="pr36-crono">
                  <h4 className="pr36-crono-titulo">
                    <IconoCalendario size={14} />
                    Cronograma de la convocatoria
                  </h4>
                  <ol className="pr36-crono-lista">
                    {sel.cronograma.map((hito) => (
                      <li
                        key={hito.campo}
                        className={`pr36-crono-hito is-${hito.estado}${hito.tono ? ` ${hito.tono}` : ''}`}
                      >
                        <span className="pr36-crono-punto">
                          {hito.estado === 'cumplido' && <IconoCheck size={12} />}
                        </span>
                        <div className="pr36-crono-texto">
                          <div className="pr36-crono-label">{hito.label}</div>
                          <div className="pr36-crono-fecha">{hito.texto}</div>
                        </div>
                        {hito.falta && <span className="pr36-crono-falta">{hito.falta}</span>}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Documentos: bases del procedimiento y anexos de la entidad */}
                <div className="pr36-docs">
                  <h4 className="pr36-docs-titulo">
                    <IconoClip size={14} />
                    Documentos de la convocatoria
                    {sel.docs.length > 1 && (
                      <span className="pr36-docs-cuenta">{sel.docs.length}</span>
                    )}
                  </h4>

                  {sel.docs.length === 0 ? (
                    <p className="pr36-docs-vacio">
                      La entidad no publicó documentos para este procedimiento.
                    </p>
                  ) : (
                    <div className="pr36-docs-lista">
                      {(verTodosDocs ? sel.docs : sel.docs.slice(0, DOCS_VISIBLES)).map((doc) => (
                        <button
                          type="button"
                          key={doc.url}
                          className={`pr36-doc is-${doc.tipo}${doc.esBases ? ' is-bases' : ''}`}
                          onClick={() => abrirDoc(doc.url)}
                          title={doc.nombre}
                        >
                          <span className="pr36-doc-ic"><IconoArchivo /></span>
                          <span className="pr36-doc-main">
                            <span className="pr36-doc-title">{doc.nombre}</span>
                            <span className="pr36-doc-sub">
                              {doc.etiqueta} · se abre en una pestaña nueva
                            </span>
                          </span>
                          <IconoSalida />
                        </button>
                      ))}

                      {sel.docs.length > DOCS_VISIBLES && (
                        <button
                          type="button"
                          className="pr36-docs-mas"
                          onClick={() => setVerTodosDocs((v) => !v)}
                        >
                          {verTodosDocs
                            ? 'Ver menos documentos'
                            : `Ver los ${sel.docs.length - DOCS_VISIBLES} documentos restantes`}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <dl className="pr36-dl">
                  <div>
                    <dt>Código</dt>
                    <dd className="is-mono">{seleccionada.id_procedimiento ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Lugar</dt>
                    <dd>{sel.lugar}</dd>
                  </div>
                  <div>
                    <dt>Catálogo CUBSO</dt>
                    <dd>{seleccionada.detCubso || '—'}</dd>
                  </div>
                  {/* La fecha de convocatoria no se repite aquí: es el primer
                      hito del cronograma. */}
                  <div>
                    <dt>Moneda</dt>
                    <dd>{seleccionada.moneda || '—'}</dd>
                  </div>
                </dl>

                <details className="pr36-detalles">
                  <summary>Descripción completa del proceso</summary>
                  <p>{seleccionada.descripcion_proceso || 'Sin descripción registrada.'}</p>
                </details>
              </div>

              {/* Columna de compra */}
              <aside className="pr36-compra">
                <div className="pr36-compra-cab">
                  <span className="pr36-compra-label">Cuantía estimada</span>
                  <span className="pr36-compra-estado">Entrega en {ENTREGA}</span>
                </div>

                <CuantiaTapada
                  className="pr36-tapado pr36-compra-tapado"
                  texto={<><IconoCandado size={15} />Se desbloquea al solicitarla</>}
                />

                <div className="pr36-compra-conf">
                  <div className="pr36-anillo" style={{ background: sel.anillo }}>
                    <span>{sel.probTxt}</span>
                  </div>
                  <div>
                    <div className="pr36-compra-titulo">Confianza {sel.nivel} del modelo</div>
                    <div className="pr36-compra-nota">
                      {sel.probTxt} % de acierto estimado para este ítem
                    </div>
                  </div>
                </div>

                <ul className="pr36-compra-lista">
                  <li><span>✓</span>Límite inferior y superior, con 95 % de confianza</li>
                  <li><span>✓</span>Entrega por WhatsApp en {ENTREGA}, sin registro</li>
                  <li><span>✓</span>Si el valor cae fuera del rango, te devolvemos el {GARANTIA} %</li>
                </ul>

                <div className="pr36-compra-precio">
                  <span className="pr36-compra-cifra">S/ {PRECIO}</span>
                  <span className="pr36-compra-unidad">pago único</span>
                </div>

                <a
                  className="pr36-wa"
                  href={enlaceWhatsApp(seleccionada, correo)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconoWhatsApp />
                  Solicitar cuantía por WhatsApp
                </a>

                <div className="pr36-compra-pie">
                  Se abre el chat al {WHATSAPP_VISIBLE} con los datos de esta convocatoria ya
                  escritos.
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrediccionLicitaciones;
